import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Pill, Search, Plus, Edit2, Trash2, X } from 'lucide-react';
import Pagination from './Pagination';
import ConfirmModal from './ConfirmModal';
import Toast from './Toast';
import SearchableSelect from './SearchableSelect';

const Medicines = () => {
  const [medicines, setMedicines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [paginationData, setPaginationData] = useState(null);

  // Lists for dropdowns
  const [generics, setGenerics] = useState([]);
  const [companies, setCompanies] = useState([]);

  // Modal states
  const [modal, setModal] = useState({ isOpen: false, type: null, medicine: null });
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, medicine: null });
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState(null);

  // Form state
  const initialForm = { name: '', genericId: '', companyId: '', strength: '', dosage_form: '', price: '', isActive: true, packDescription: '', packQuantity: '' };
  const [formData, setFormData] = useState(initialForm);

  const fetchMedicines = useCallback(async (currentPage = 1, searchQuery = search) => {
    try {
      setLoading(true);
      const res = await api.get(`/api/admin/medicines?page=${currentPage}&limit=10&search=${searchQuery}`);
      setMedicines(res.data.data);
      setPaginationData(res.data.pagination);
    } catch (err) {
      console.error('Error fetching medicines:', err);
    } finally {
      setLoading(false);
    }
  }, [search]);

  const fetchDropdowns = useCallback(async () => {
    try {
      const [genRes, compRes] = await Promise.all([
        api.get('/api/medicines/generics'),
        api.get('/api/medicines/companies')
      ]);
      setGenerics(genRes.data.data || []);
      setCompanies(compRes.data.data || []);
    } catch (err) {
      console.error('Error fetching dropdowns:', err);
    }
  }, []);

  useEffect(() => {
    fetchDropdowns();
  }, [fetchDropdowns]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchMedicines(page, search);
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [page, search, fetchMedicines]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const openAddModal = () => {
    setFormData(initialForm);
    setModal({ isOpen: true, type: 'add', medicine: null });
  };

  const openEditModal = async (medicine) => {
    // Fetch fresh data including packaging details
    try {
      const res = await api.get(`/api/admin/medicines/${medicine.id}`);
      const m = res.data.data;
      setFormData({
        name: m.name,
        genericId: m.genericId || '',
        companyId: m.companyId || '',
        strength: m.strength || '',
        dosage_form: m.dosage_form || '',
        price: m.price !== null && m.price !== undefined ? String(m.price) : '',
        isActive: m.isActive,
        packDescription: m.packDescription || '',
        packQuantity: m.packQuantity || ''
      });
    } catch {
      // Fallback to row data if fetch fails
      setFormData({
        name: medicine.name,
        genericId: medicine.genericId || '',
        companyId: medicine.companyId || '',
        strength: medicine.strength || '',
        dosage_form: medicine.dosage_form || '',
        price: medicine.price !== null && medicine.price !== undefined ? String(medicine.price) : '',
        isActive: medicine.isActive,
        packDescription: '',
        packQuantity: ''
      });
    }
    setModal({ isOpen: true, type: 'edit', medicine });
  };

  const openDeleteModal = (medicine) => {
    setConfirmModal({ isOpen: true, medicine });
  };

  const closeModal = () => {
    setModal({ isOpen: false, type: null, medicine: null });
    setFormData(initialForm);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      if (modal.type === 'add') {
        await api.post('/api/admin/medicines', formData);
      } else if (modal.type === 'edit') {
        await api.put(`/api/admin/medicines/${modal.medicine.id}`, formData);
      }
      await Promise.all([
        fetchMedicines(page, search),
        fetchDropdowns()
      ]);
      setToast({ message: `Medicine ${modal.type === 'add' ? 'added' : 'updated'} successfully`, type: 'success' });
      closeModal();
    } catch (err) {
      setToast({ message: `Error saving medicine: ${err.response?.data?.message || err.message}`, type: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    const id = confirmModal.medicine.id;
    setConfirmModal({ isOpen: false, medicine: null });
    setActionLoading(true);
    try {
      await api.delete(`/api/admin/medicines/${id}`);
      await fetchMedicines(page, search);
      setToast({ message: 'Medicine deleted successfully', type: 'success' });
    } catch (err) {
      setToast({ message: `Error deleting medicine: ${err.response?.data?.message || err.message}`, type: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="space-y-8 sm:space-y-10 relative">

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title="Delete Medicine?"
        message={`Are you sure you want to delete ${confirmModal.medicine?.name}? This action cannot be undone.`}
        type="danger"
        confirmText="Yes, Delete"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmModal({ isOpen: false, medicine: null })}
      />

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Add/Edit Modal */}
      {modal.isOpen && (
        <div className="fixed inset-0 w-full h-full z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={closeModal} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-5 sm:p-6 animate-[fadeIn_0.15s_ease-out] max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                {modal.type === 'add' ? 'Add New Medicine' : 'Edit Medicine'}
              </h3>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600 flex-shrink-0 ml-2">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleFormSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Name *</label>
                <input
                  required
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="e.g. Napa Extra"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="relative z-20">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Generic *</label>
                  <SearchableSelect
                    options={generics}
                    value={formData.genericId}
                    onChange={(val) => setFormData({ ...formData, genericId: val })}
                    onAdd={(name) => {
                      const exists = generics.find(g => g.name.toLowerCase() === name.toLowerCase());
                      if (exists) {
                        setFormData({ ...formData, genericId: exists.id });
                      } else {
                        const newGen = { id: name, name }; // Use name as temp ID
                        setGenerics([...generics, newGen]);
                        setFormData({ ...formData, genericId: name });
                      }
                    }}
                    placeholder="Select Generic"
                    required={true}
                  />
                </div>
                <div className="relative z-10">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Company *</label>
                  <SearchableSelect
                    options={companies}
                    value={formData.companyId}
                    onChange={(val) => setFormData({ ...formData, companyId: val })}
                    onAdd={(name) => {
                      const exists = companies.find(c => c.name.toLowerCase() === name.toLowerCase());
                      if (exists) {
                        setFormData({ ...formData, companyId: exists.id });
                      } else {
                        const newComp = { id: name, name }; // Use name as temp ID
                        setCompanies([...companies, newComp]);
                        setFormData({ ...formData, companyId: name });
                      }
                    }}
                    placeholder="Select Company"
                    required={true}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Strength</label>
                  <input
                    type="text"
                    value={formData.strength}
                    onChange={(e) => setFormData({ ...formData, strength: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="e.g. 500mg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Dosage Form</label>
                  <input
                    type="text"
                    value={formData.dosage_form}
                    onChange={(e) => setFormData({ ...formData, dosage_form: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="e.g. Tablet"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Price (MRP) *</label>
                <input
                  required
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="0.00"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-gray-100 pt-4 mt-2">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Pack Type</label>
                  <input
                    type="text"
                    value={formData.packDescription}
                    onChange={(e) => setFormData({ ...formData, packDescription: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                    placeholder="e.g. Box of 100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Qty per Pack</label>
                  <input
                    type="number"
                    value={formData.packQuantity}
                    onChange={(e) => setFormData({ ...formData, packQuantity: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                    placeholder="100"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 mt-4">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                />
                <label htmlFor="isActive" className="text-sm text-gray-700 font-medium">Active (Available for search)</label>
              </div>

              <div className="pt-4 flex gap-3">
                <button type="button" onClick={closeModal} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-semibold hover:bg-gray-50">
                  Cancel
                </button>
                <button type="submit" disabled={actionLoading} className="flex-1 py-2.5 rounded-xl bg-primary-600 text-white font-semibold hover:bg-primary-700 disabled:opacity-50">
                  {actionLoading ? 'Saving...' : 'Save Medicine'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Page header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Master Medicines</h1>

        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full md:w-auto">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search by name..."
              className="pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 w-full md:w-64 transition-all"
              value={search}
              onChange={handleSearchChange}
            />
          </div>
          <button onClick={openAddModal} className="btn btn-primary flex items-center justify-center gap-2 whitespace-nowrap py-2.5 sm:py-2">
            <Plus size={20} />
            <span>Add Medicine</span>
          </button>
        </div>
      </div>

      {actionLoading && !modal.isOpen && (
        <div className="flex items-center gap-2 text-sm text-primary-600 bg-primary-50 p-3 rounded-xl">
          <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
          Processing...
        </div>
      )}

      {/* Table / Card View */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        {/* Desktop Table View */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full text-left min-w-[720px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 sm:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-4 sm:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Generic</th>
                <th className="px-4 sm:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Company</th>
                <th className="px-4 sm:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">Strength</th>
                <th className="px-4 sm:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">Type</th>
                <th className="px-4 sm:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Pack Size</th>
                <th className="px-4 sm:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Price</th>
                <th className="px-4 sm:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 sm:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="9" className="px-6 py-10 text-center text-gray-500">Loading medicines...</td>
                </tr>
              ) : medicines.map((m) => (
                <tr key={m.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 sm:px-6 py-4">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="p-1.5 sm:p-2 bg-blue-50 text-blue-600 rounded-lg flex-shrink-0">
                        <Pill size={16} />
                      </div>
                      <span className="font-semibold text-gray-900 text-sm sm:text-base">{m.name}</span>
                    </div>
                  </td>
                  <td className="px-4 sm:px-6 py-4 text-gray-600 italic text-sm hidden md:table-cell">{m.genericName || '—'}</td>
                  <td className="px-4 sm:px-6 py-4 text-gray-600 text-sm hidden lg:table-cell">{m.companyName || '—'}</td>
                  <td className="px-4 sm:px-6 py-4 text-gray-500 text-sm hidden sm:table-cell">{m.strength || '—'}</td>
                  <td className="px-4 sm:px-6 py-4 hidden sm:table-cell">
                    <span className="bg-gray-100 text-gray-600 text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wider">
                      {m.dosage_form || '—'}
                    </span>
                  </td>
                  <td className="px-4 sm:px-6 py-4 text-gray-600 text-sm hidden lg:table-cell">
                    {m.packDescription && m.packQuantity ? `${m.packDescription} (${m.packQuantity})` : m.packDescription || m.packQuantity || '—'}
                  </td>
                  <td className="px-4 sm:px-6 py-4 font-semibold text-gray-900 text-sm whitespace-nowrap">
                    {parseFloat(m.price || '0') > 0
                      ? `৳${parseFloat(m.price).toFixed(2)}`
                      : <span className="text-orange-400 font-medium text-xs">Not set</span>
                    }
                  </td>
                  <td className="px-4 sm:px-6 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wider uppercase border whitespace-nowrap ${m.isActive ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                      {m.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 sm:px-6 py-4 text-right">
                    <div className="flex justify-end gap-1 sm:gap-2">
                      <button onClick={() => openEditModal(m)} className="p-1.5 sm:p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Edit">
                        <Edit2 size={16} />
                      </button>
                      <button onClick={() => openDeleteModal(m)} className="p-1.5 sm:p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && medicines.length === 0 && (
                <tr>
                  <td colSpan="9" className="px-6 py-10 text-center text-gray-500">
                    No medicines found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="sm:hidden divide-y divide-gray-100">
          {loading ? (
            <div className="p-6 text-center text-gray-500">Loading medicines...</div>
          ) : medicines.map((m) => (
            <div key={m.id} className="p-4 space-y-3">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                    <Pill size={18} />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">{m.name}</h4>
                    <p className="text-xs text-gray-500 italic">{m.genericName || 'No Generic'}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openEditModal(m)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                    <Edit2 size={18} />
                  </button>
                  <button onClick={() => openDeleteModal(m)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm pt-2">
                <div>
                  <p className="text-gray-500 text-[10px] uppercase font-bold tracking-wider">Strength / Type</p>
                  <p className="font-medium text-gray-700">{m.strength || '—'} / {m.dosage_form || '—'}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-[10px] uppercase font-bold tracking-wider text-right">Price (MRP)</p>
                  <p className="font-bold text-gray-900 text-right">
                    {parseFloat(m.price || '0') > 0
                      ? `৳${parseFloat(m.price).toFixed(2)}`
                      : <span className="text-orange-400 font-medium text-xs">Not set</span>
                    }
                  </p>
                </div>
              </div>

              {(m.packDescription || m.packQuantity) && (
                <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                  <p className="text-blue-700 text-[10px] uppercase font-bold tracking-wider mb-1">Package Size</p>
                  <p className="text-blue-900 font-medium text-sm">
                    {m.packDescription && m.packQuantity ? `${m.packDescription} (${m.packQuantity})` : m.packDescription || m.packQuantity || '—'}
                  </p>
                </div>
              )}

              <div className="flex items-center justify-between pt-2 border-t border-gray-50">
                <span className="text-xs text-gray-500">{m.companyName || 'No Company'}</span>
                <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wider uppercase border ${m.isActive ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                  {m.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          ))}
          {!loading && medicines.length === 0 && (
            <div className="p-6 text-center text-gray-500">No medicines found.</div>
          )}
        </div>

        <Pagination pagination={paginationData} onPageChange={setPage} />
      </div>
    </div>
  );
};

export default Medicines;
